import { useModifyAudioDevices } from '@hakimo-ui/hakimo/data-access';
import { AudioDevice } from '@hakimo-ui/hakimo/types';
import {
  eventTracker,
  toast,
  useCanModifyAudioDevice,
} from '@hakimo-ui/hakimo/util';
import { Button, Modal, Toggle } from '@hakimo-ui/shared/ui-base';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';

interface Props {
  audioDevice: AudioDevice;
}

export function SPSToggle(props: Props) {
  const {
    isSPSEnabled = false,
    id,
    tenantId,
    location,
    cameras,
  } = props.audioDevice;
  const [showModal, setShowModal] = useState(false);
  const onModalClose = () => setShowModal(false);
  const canModifyAudioDevice = useCanModifyAudioDevice();
  const [enabled, setEnabled] = useState(isSPSEnabled);

  const modifyAudioDeviceMutation = useModifyAudioDevices(
    () => {
      toast('SPS setting updated', { type: 'success' });
      onModalClose();
      setEnabled((prev) => !prev);
    },
    () => {
      toast('Failed to update SPS setting', { type: 'error' });
      onModalClose();
    }
  );

  useEffect(() => {
    setEnabled(isSPSEnabled);
  }, [isSPSEnabled]);

  const onToggle = () => setShowModal(true);

  const onSubmitAction = () => {
    const payload = {
      id,
      tenantId,
      isSPSEnabled: !isSPSEnabled,
      location,
    };
    modifyAudioDeviceMutation.mutate(payload);
  };

  const modalActions = (
    <>
      <Button
        variant="primary"
        onClick={onSubmitAction}
        disabled={modifyAudioDeviceMutation.isLoading}
        onSideEffect={eventTracker('update_sps_setting')}
        loading={modifyAudioDeviceMutation.isLoading}
      >
        {enabled ? 'Disable' : 'Enable'}
      </Button>
      <Button onClick={onModalClose}>Cancel</Button>
    </>
  );

  if (!canModifyAudioDevice) {
    return enabled ? (
      <div className="text-status-green h-5 w-5">
        <CheckCircleIcon />
      </div>
    ) : (
      <div className="text-status-red h-5 w-5">
        <XCircleIcon />
      </div>
    );
  }

  return (
    <div>
      <Toggle enabled={enabled} onChange={onToggle} />
      {showModal && (
        <Modal
          onClose={onModalClose}
          title="SPS Setting"
          open={showModal}
          footer={modalActions}
        >
          <div className="p-6 leading-8">
            <div>Associated Cameras</div>
            <ul className="list-disc pl-4">
              {cameras.map((cam) => (
                <li key={cam.id}>{cam.name}</li>
              ))}
            </ul>
            <div>
              You are about to
              <span className="dark:bg-ondark-bg-2 bg-onlight-bg-2 whitespace-pre rounded-md px-2 py-1 font-bold">
                {enabled ? 'disable' : 'enable'}
              </span>
              SPS for this audio device. Are you sure?
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default SPSToggle;
